import React from 'react';
import Moment from 'moment';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { isNumber } from 'lodash';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import Chart from './chart';

export default function MeasurePage({
  data: {
    metricType,
    handsontable,
    highChartSetting,
    showComment,
    comment,
    embed,
    advancedMetric,
    advancedMetricType,
    advancedMetricTargetUnit,
    advancedMetricChartType = 'column',
    measureResults = [],
    singleTargetAggregationFunction,
    singleTargetMilestoneDate,
    singleTargetTargetAmount,
    title,
    label,
    viewType,
    performanceGraphUrl,
    summaryDetails,
    visualDescription,
    description,
    additionalText,
    additionalBottomText,
  },
  startOfFinancialYear,
}) {
  const location = useLocation();
  const queryString = location.search;
  const params = new URLSearchParams(queryString);
  const chartonly = params.get('chartonly') === 'true';
  let descriptionStyles = {};

  if (metricType === 'Highcharts') {
    const { subtitle } = JSON.parse(highChartSetting);
    const { font_size: descriptionFontSize, color: descriptionColor } = subtitle || {};
    descriptionStyles = { fontSize: descriptionFontSize, color: descriptionColor };
  }

  return (
    <section className="drilldown-layout measure-layout">
      <div className="chart-container" style={metricType === 'Summary' ? { height: '100%' } : {}} aria-label={label}>
        <Chart
          height={350}
          advancedMetric={advancedMetric}
          advancedMetricType={advancedMetricType}
          advancedMetricTargetUnit={advancedMetricTargetUnit}
          advancedMetricChartType={advancedMetricChartType}
          measureResults={measureResults}
          singleTargetAggregationFunction={singleTargetAggregationFunction}
          singleTargetMilestoneDate={singleTargetMilestoneDate}
          singleTargetTargetAmount={singleTargetTargetAmount}
          title={title}
          label={label}
          viewType={viewType || 'daily'}
          startOfFinancialYear={startOfFinancialYear}
          handsontable={handsontable}
          highChartSetting={highChartSetting}
          performanceGraphUrl={performanceGraphUrl}
          metricType={metricType}
          summaryDetails={summaryDetails}
          embed={embed}
        />
      </div>
      {!!measureResults.filter(({ dataPointX, dataPointYValue }) => Moment(dataPointX).isValid() && isNumber(parseFloat(dataPointYValue)))
        .length && (
        <ul className="additional-highcharts-legends">
          {!!singleTargetTargetAmount && (
            <li>
              <span className="target-legend-symbol" />
              <strong>Target:</strong>
              {singleTargetTargetAmount}
            </li>
          )}
          {!!singleTargetMilestoneDate && (
            <li>
              <span className="milestone-legend-symbol" />
              <strong>Milestone:</strong>
              {Moment(singleTargetMilestoneDate).format('ll')}
            </li>
          )}
        </ul>
      )}
      {!!visualDescription && (
        <div className="drilldown-description visual-description pd-custom fr-view" style={descriptionStyles}>
          <FroalaEditorView model={visualDescription} />
        </div>
      )}
      {!!description && (
        <div className="drilldown-description pd-custom fr-view">
          <FroalaEditorView model={description} />
        </div>
      )}
      {showComment && comment && !chartonly && (
        <div className="comment-section pd-custom fr-view">
          <FroalaEditorView model={comment} />
        </div>
      )}
      {!!additionalText && !chartonly && (
        <div className="drilldown-description pd-custom fr-view">
          <FroalaEditorView model={additionalText} />
        </div>
      )}
      {!!additionalBottomText && !chartonly && (
        <div className="drilldown-description pd-custom fr-view">
          <FroalaEditorView model={additionalBottomText} />
        </div>
      )}
    </section>
  );
}

MeasurePage.propTypes = {
  data: PropTypes.shape({
    metricType: PropTypes.string,
    handsontable: PropTypes.string,
    highChartSetting: PropTypes.string,
    showComment: PropTypes.bool,
    comment: PropTypes.string,
    advancedMetric: PropTypes.string,
    advancedMetricType: PropTypes.string,
    advancedMetricTargetUnit: PropTypes.string,
    advancedMetricChartType: PropTypes.string,
    measureResults: PropTypes.arrayOf(PropTypes.shape({})),
    singleTargetAggregationFunction: PropTypes.string,
    singleTargetMilestoneDate: PropTypes.string,
    singleTargetTargetAmount: PropTypes.string,
    title: PropTypes.string,
    label: PropTypes.string,
    viewType: PropTypes.string,
    performanceGraphUrl: PropTypes.string,
    embed: PropTypes.string,
    summaryDetails: PropTypes.string,
    visualDescription: PropTypes.string,
    description: PropTypes.string,
    additionalText: PropTypes.string,
    additionalBottomText: PropTypes.string,
  }).isRequired,
  startOfFinancialYear: PropTypes.string.isRequired,
};
